import { useCallback, useContext, useEffect, useState } from 'react'
import { MultiWalletTypeSelectFilter } from '@/components/Filter/common.filter'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Dialog } from '@/components/ui/dialog'
import { SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { TransactionDto, TransactionFilterKey } from 'api/Transaction/Types/Transaction'
import { Pager } from 'components/components/Pager'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import { SelectAssigneeFilter } from 'components/Filter/ClientFields/SelectAssigneeFilter'
import {
  SelectClientFilter,
  SelectClientModalOpenEvent,
} from 'components/Filter/ClientFields/SelectClientFilter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getDepositWithdrawalFields } from 'components/Filter/getDepositWithdrawalFields'
import {
  SelectWalletFilter,
  SelectWalletModalOpenEvent,
} from 'components/Filter/WalletFields/SelectWalletFilter'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import {
  createGridState,
  normalizeFilter,
  useGridFilter,
  useGridState,
  useGridStatusState,
} from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  GridFilterActionState,
  initialState,
  resetGridFilters,
  selectGridFilterByFilterId,
  setGridFilters,
} from 'store/grid'
import { fetchCountriesAction, fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import {
  fetchPaymentProviderAction,
  fetchTransactionStatusAction,
  fetchTransactionTypeAction,
} from 'store/transactions/transaction.action'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { PaymentApiClient } from 'utils/api/paymentApi'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery, FilterLogic } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'
import { isTransactionCompletedStatus } from 'utils/transaction/status.index'

import { CustomColumnMenu } from '../Records/Clients/CustomColumnMenu'
import { SelectClientEvent, SelectClientTable } from '../Records/Clients/SelectClientTable'
import SelectWalletTable, { SelectWalletEvent } from '../Records/Wallets/SelectWalletTable'
import { normalizeTransactions, normalizeTransactionsSortQuery } from './normalizer'
import {
  SelectTransactionsAssignTable,
  SelectTransactionsEvent,
} from './SelectTransactionAssignTable'
import WalletTransactionReversePage from './TransactionReverse/WalletTransactionReversePage'
import walletDepositWithdrawalColumn, { GridColumn } from './walletDepositWithdrawalColumn'

const useMapStateToProps = () => ({
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  transactionStatus: useAppSelector((state) => state.transactions.status),
  transactionTypes: useAppSelector((state) => state.transactions.types),
  paymentProviders: useAppSelector((state) => state.transactions.paymentProviders),
  countries: useAppSelector((state) => state.identity.countries),
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
  filters: useAppSelector((state) => state.identity.filter),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const paymentApiClient = useApiClient(PaymentApiClient)
  return {
    fetchTransactionTypes: async () => dispatch(fetchTransactionTypeAction(walletsApiClient)),
    fetchPaymentProviders: async () => dispatch(fetchPaymentProviderAction(paymentApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    fetchCountries: async () => dispatch(fetchCountriesAction(identityApiClient)),
    fetchTransactionStatus: async () => dispatch(fetchTransactionStatusAction(walletsApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}

const DepositWithdrawalTransactionOverview = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [queryFiltersParsed, setQueryFiltersParsed] = useState<boolean>(false)

  const permissions = auth?.permissions || []
  const [columns, setColumns] = useState<GridColumn[]>([])
  const [transactions, setTransactions] = useState<TransactionDto[]>([])

  const [isClientModalOpen, setClientModalOpen] = useState<boolean>(false)
  const [isWalletModalOpen, setWalletModalOpen] = useState<boolean>(false)
  const [isAssigneeModalOpen, setAssigneeModalOpen] = useState<boolean>(false)
  const [reverseTransactionModal, setReverseTransactionModal] = useState<{
    data?: TransactionDto
    visible: boolean
  }>({ data: undefined, visible: false })

  const [clientModalFilterId, setClientModalFilterId] = useState<string>()
  const [assignModalFilterId, setAssignModalFilterId] = useState<string>()
  const [walletModalFilterId, setWalletModalFilterId] = useState<string>()

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const {
    transactionStatus,
    transactionTypes,
    paymentProviders,
    tickmillCompanies,
    countries,
    gridFilterByFilterIdSelector,
  } = useMapStateToProps()
  const {
    fetchTransactionStatus,
    fetchTransactionTypes,
    fetchTickmillCompanies,
    fetchPaymentProviders,
    fetchCountries,
  } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      const { defaultFilters } = createDepositWithdrawalTransactionsQuery()
      if (!!defaultFilters.filters.length && !queryFiltersParsed) {
        setQueryFiltersParsed(true)
        gridStatusActions.setCreateMode(true)
        gridStatusActions.setFilterMode(true)
        gridActions.setGridFilters({
          filter: normalizeFilter(defaultFilters),
        })
      }

      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<TransactionDto[]>> => {
          const searchQuery = getSearchQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await walletsApiClient.getTransactions('deposit-withdrawals', {
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(transactionStatus)) {
            await fetchTransactionStatus()
          }
          if (isEmpty(transactionTypes)) {
            await fetchTransactionTypes()
          }
          if (isEmpty(paymentProviders)) {
            await fetchPaymentProviders()
          }
          if (isEmpty(tickmillCompanies)) {
            await fetchTickmillCompanies()
          }
          if (isEmpty(countries)) {
            await fetchCountries()
          }

          setColumns(walletDepositWithdrawalColumn)
          if (response?.items) {
            setTransactions(normalizeTransactions(response.items))
          }
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<TransactionDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getSearchQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const gridFilter = filter ?? gridState.filter
      const { defaultFilters } = createDepositWithdrawalTransactionsQuery()
      return (
        buildQuery({
          ...gridFilter,
          logic: gridFilter.logic,
          defaultFilters,
        }) ?? null
      )
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeTransactionsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (query?: PageQuery) => {
      const searchQuery = query?.search !== undefined ? query.search : getSearchQuery()
      const sortQuery = query?.sort ?? getSort(gridState.sort)
      const pageIndex = query?.pageIndex ?? gridState.pageIndex ?? 1
      const pageSize = query?.pageSize ?? gridState.pageSize ?? 10
      const take = pageSize
      const skip = (pageIndex - 1) * pageSize

      const response = await walletsApiClient.getTransactions('deposit-withdrawals', {
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setTransactions(normalizeTransactions(response.items))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getSearchQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await walletsApiClient.getTransactions('deposit-withdrawals', {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    gridStatusActions.setFetching(false)

    if (response?.items) {
      setTransactions(normalizeTransactions(response.items))
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    gridStatusActions.setFetching(true)
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getTransactions('deposit-withdrawals', {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    gridStatusActions.setFetching(false)
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getTransactions('deposit-withdrawals', {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    gridStatusActions.setFetching(false)

    if (response?.items) {
      setTransactions(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }
  const handleGridPageChange = async (page: number) => {
    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page

    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await walletsApiClient.getTransactions('deposit-withdrawals', {
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setTransactions(normalizeTransactions(response.items))
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleColumnsSubmit = (columnsState: GridColumn[]) => {
    setColumns(columnsState)
  }

  const handleReverseTransaction = (transaction: TransactionDto) => {
    setReverseTransactionModal({ data: transaction, visible: true })
  }

  const handleSelectedClients = (event: SelectClientEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleSelectedWallets = (event: SelectWalletEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleSelectedUsersAssign = (event: SelectTransactionsEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
      setAssigneeModalOpen(false)
    }
  }

  const handleClientModalOpen = (event: SelectClientModalOpenEvent) => {
    setClientModalOpen(true)
    setClientModalFilterId(event.filterId)
  }

  const handleClientModalClose = () => {
    setClientModalOpen(false)
    setClientModalFilterId(undefined)
  }

  const handleWalletModalOpen = (event: SelectWalletModalOpenEvent) => {
    setWalletModalOpen(true)
    setWalletModalFilterId(event.filterId)
  }

  const handleWalletModalClose = () => {
    setWalletModalOpen(false)
    setWalletModalFilterId(undefined)
  }

  const handleReverseCompleteTransactionClose = async () => {
    setReverseTransactionModal({ data: undefined, visible: false })
    await filterFetch()
  }

  const handleAssigneeModalOpen = (event: SelectWalletModalOpenEvent) => {
    setAssigneeModalOpen(true)
    setAssignModalFilterId(event.filterId)
  }

  const handleSelectAssignUsersModalClose = () => {
    setAssigneeModalOpen(false)
    setAssignModalFilterId(undefined)
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <>
      {isClientModalOpen && (
        <Dialog open={isClientModalOpen} onOpenChange={(open) => !open && handleClientModalClose()}>
          <SelectClientTable
            filterId={clientModalFilterId}
            onSelect={handleSelectedClients}
            onClose={handleClientModalClose}
          />
        </Dialog>
      )}
      {isAssigneeModalOpen && (
        <Dialog
          open={isAssigneeModalOpen}
          onOpenChange={(open) => !open && handleSelectAssignUsersModalClose()}
        >
          <SelectTransactionsAssignTable
            filterId={assignModalFilterId}
            onMultipleSelect={handleSelectedUsersAssign}
            selectText='Select'
          />
        </Dialog>
      )}
      {isWalletModalOpen && (
        <Modal width={900} visible={isWalletModalOpen} onClose={handleWalletModalClose}>
          <SelectWalletTable
            filterId={walletModalFilterId}
            onSelect={handleSelectedWallets}
            onClose={handleWalletModalClose}
          />
        </Modal>
      )}
      {reverseTransactionModal.visible && !!reverseTransactionModal.data && (
        <Modal
          width={600}
          padding='3rem'
          visible={reverseTransactionModal.visible}
          onClose={handleReverseCompleteTransactionClose}
        >
          <WalletTransactionReversePage
            transaction={reverseTransactionModal.data}
            onClose={handleReverseCompleteTransactionClose}
          />
        </Modal>
      )}
      <Breadcrumbs
        crumbs={[
          {
            title: 'Transactions - Deposit/Withdrawal',
            link: links.transactionDepositWithdrawal.url,
          },
        ]}
      />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.WALLET_DEPOSIT_WITHDRAWAL}
        filterFields={[
          {
            name: TransactionFilterKey.walletTransactionWalletClient,
            label: 'Client',
            filter: (clientFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectClientFilter {...clientFilter} onClientModalOpen={handleClientModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: TransactionFilterKey.walletTransactionWallet,
            label: 'Wallet',
            filter: (walletFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectWalletFilter {...walletFilter} onWalletModalOpen={handleWalletModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: TransactionFilterKey.assignee,
            label: 'Assignee',
            filter: (assigneeFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectAssigneeFilter
                {...assigneeFilter}
                onAssignModalOpen={handleAssigneeModalOpen}
              />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: 'WalletTypeId',
            label: 'Wallet Type',
            filter: MultiWalletTypeSelectFilter,
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          ...getDepositWithdrawalFields(),
        ]}
        containerClassName='wallet-table__filter'
        getQuery={getSearchQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport
          data={transactions}
          hasPermissions={hasPermission(permissions, PermissionsEnum.CanExportTransactionsModule)}
        >
          <Grid
            {...gridState}
            resizable
            scrollable='scrollable'
            data={transactions}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {columns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column
                  key={idx + 2}
                  columnMenu={(gridProps) => (
                    <CustomColumnMenu
                      {...gridProps}
                      columns={columns}
                      onColumnsSubmit={handleColumnsSubmit}
                    />
                  )}
                  {...column}
                />
              ))}
            <Column
              width={60}
              title=''
              locked
              cell={(gridProps: GridCellProps) => {
                if (isTransactionCompletedStatus(gridProps.dataItem.state.id)) {
                  return (
                    <td className={gridProps.className} style={gridProps.style}>
                      <MoreButton
                        items={[
                          {
                            name: 'Reverse',
                            disabled: !hasPermission(
                              permissions,
                              PermissionsEnum.CanReverseWalletDepositWithdrawalTransaction
                            ),
                            onClick: () => handleReverseTransaction(gridProps.dataItem),
                          },
                        ]}
                      />
                    </td>
                  )
                }

                return <td className={gridProps.className} style={gridProps.style} />
              }}
            />
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}

const createDepositWithdrawalTransactionsQuery = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const initialGridState = initialState()

  const queryFilters = []
  for (const [key, value] of urlParams.entries()) {
    if (key in TransactionFilterKey)
      queryFilters.push({
        field: TransactionFilterKey[key as keyof typeof TransactionFilterKey],
        operator: '==',
        value,
      })
  }

  return {
    ...initialGridState.filter,
    logic: initialGridState.filter.logic,
    defaultFilters: {
      logic: FilterLogic.AND,
      filters: queryFilters,
    },
  }
}

export default DepositWithdrawalTransactionOverview
